/*
 * Cart cross-sell block (snippets/cart-cross-sell.liquid).
 *
 * The block is rendered server-side with the cart drawer / cart page section, so after an add we
 * only re-render those sections — no client-side state survives, and none is needed. Every add
 * carries _source=cart_fbt (plus rule and anchor) as hidden line item properties; that is what
 * the cross-sell job's --report counts.
 */
if (!customElements.get('cart-cross-sell')) {
  const GROUP_ATTRIBUTE = '_cross_sell_group';
  const seenImpressions = new Set();
  let groupAssignment = null; // one /cart/update per page, even with drawer + cart page block
  // Where focus belongs after an add. The drawer can be rebuilt more than once after an add (the
  // theme, and Rebuy's cart listener in the footer), and each rebuild drops focus to <body> — so
  // every freshly connected block checks this and restores focus itself.
  let pendingFocus = null;

  const publishAnalytics = (name, payload) => {
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        Shopify.analytics.publish(name, payload);
      }
    } catch (e) {
      // analytics must never break the cart
    }
  };

  // Focus without letting the browser scroll containers that are meant to be clipped: in the
  // drawer, .drawer__inner is overflow:hidden, yet focus() scrolls it anyway and pushes the totals
  // and checkout button out of place. Scroll only the list the card sits in, then focus.
  const focusInPlace = (element) => {
    if (!element) return;
    element.focus({ preventScroll: true });
    const list = element.closest('cart-drawer-items');
    if (list && list.scrollHeight > list.clientHeight) {
      const box = element.getBoundingClientRect();
      const frame = list.getBoundingClientRect();
      if (box.bottom > frame.bottom) list.scrollTop += box.bottom - frame.bottom + 16;
      else if (box.top < frame.top) list.scrollTop -= frame.top - box.top + 16;
    } else if (!list) {
      element.scrollIntoView({ block: 'nearest' });
    }
    const inner = element.closest('.drawer__inner');
    if (inner && getComputedStyle(inner).overflowY === 'hidden') inner.scrollTop = 0;
  };

  class CartCrossSell extends HTMLElement {
    connectedCallback() {
      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.status = this.querySelector('[data-cross-sell-status]');
      this.restoreFocus();

      if (this.hidden) {
        this.assignGroup();
      } else {
        this.trackImpression();
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
    }

    get context() {
      return this.dataset.context;
    }

    items() {
      return [...this.querySelectorAll('[data-cross-sell-item]')];
    }

    // Holdout: a share of carts never sees the block, so the job can compare order values.
    // The group lives in a cart attribute, which Liquid reads on every later render.
    assignGroup() {
      const holdout = Number(this.dataset.holdout || 0);
      if (!holdout || this.dataset.group) return;
      if (!groupAssignment) {
        const group = Math.random() * 100 < holdout ? 'control' : 'test';
        groupAssignment = fetch(routes.cart_update_url, {
          ...fetchConfig(),
          body: JSON.stringify({ attributes: { [GROUP_ATTRIBUTE]: group } }),
        })
          .then((response) => (response.ok ? group : 'test'))
          .catch(() => 'test');
      }
      groupAssignment.then((group) => {
        this.dataset.group = group;
        if (group === 'control') {
          this.remove();
          return;
        }
        this.hidden = false;
        this.trackImpression();
      });
    }

    trackImpression() {
      const products = this.items().map((item) => item.dataset.productId);
      const signature = `${this.context}:${products.join(',')}`;
      if (!products.length || seenImpressions.has(signature)) return;
      seenImpressions.add(signature);
      publishAnalytics('cart_cross_sell_viewed', {
        context: this.context,
        products,
        rules: this.items().map((item) => item.dataset.rule),
      });
    }

    onChange(event) {
      // The block sits inside <cart-items>/<cart-drawer-items>, whose change handler reads every
      // change as a quantity edit and would post a broken /cart/change. Keep ours to ourselves.
      event.stopPropagation();
      const picker = event.target.closest('[data-cross-sell-picker]');
      if (!picker) return;
      const item = picker.closest('[data-cross-sell-item]');
      const group = event.target.closest('[data-cross-sell-option]');
      this.setError(item, '');
      this.syncPicker(item, Number(group?.dataset.crossSellOption ?? -1));
    }

    // Current choice per option index; '' where nothing is chosen yet.
    static selection(item) {
      return [...item.querySelectorAll('[data-cross-sell-option]')].map((group) => {
        const select = group.querySelector('select');
        if (select) return select.value;
        return group.querySelector('input:checked')?.value || '';
      });
    }

    static variants(item) {
      if (!item._variants) {
        try {
          item._variants = JSON.parse(item.querySelector('[data-cross-sell-variants]')?.textContent || '[]');
        } catch (e) {
          item._variants = [];
        }
      }
      return item._variants;
    }

    // Values with no available variant at all are disabled. Values that only clash with the
    // current choice stay clickable but struck through (like Dawn's pills): picking one drops the
    // conflicting choices, so a shopper can always get from one combination to any other.
    syncPicker(item, changedIndex = -1) {
      const variants = CartCrossSell.variants(item);
      const groups = [...item.querySelectorAll('[data-cross-sell-option]')];
      let chosen = CartCrossSell.selection(item);
      const fits = (variant, skip) => chosen.every((value, i) => i === skip || !value || variant.options[i] === value);

      if (changedIndex >= 0 && !variants.some((v) => v.available && fits(v, -1))) {
        groups.forEach((group, i) => {
          if (i === changedIndex) return;
          group.querySelectorAll('input:checked').forEach((input) => (input.checked = false));
          const select = group.querySelector('select');
          if (select) select.value = '';
        });
        chosen = CartCrossSell.selection(item);
      }

      groups.forEach((group, index) => {
        const everAvailable = new Set(variants.filter((v) => v.available).map((v) => v.options[index]));
        const fitting = new Set(variants.filter((v) => v.available && fits(v, index)).map((v) => v.options[index]));
        group.querySelectorAll('input, option[value]:not([value=""])').forEach((control) => {
          control.disabled = !everAvailable.has(control.value);
          control.classList.toggle('is-unavailable', !control.disabled && !fitting.has(control.value));
        });
      });

      delete item.dataset.variantId;
      delete item.dataset.unavailable;
      if (chosen.every(Boolean)) {
        const variant = variants.find((v) => fits(v, -1));
        if (variant && variant.available) {
          item.dataset.variantId = variant.id;
          const price = item.querySelector('[data-cross-sell-price]');
          if (price) price.textContent = variant.price;
        } else {
          item.dataset.unavailable = 'true';
        }
      }
    }

    toggle(item, button) {
      const picker = item.querySelector('[data-cross-sell-picker]');
      const open = button.getAttribute('aria-expanded') !== 'true';
      button.setAttribute('aria-expanded', String(open));
      item.classList.toggle('is-open', open);
      picker.hidden = !open;
      if (open) {
        this.syncPicker(item);
        focusInPlace(picker.querySelector('input:not([disabled]), select'));
      }
    }

    onClick(event) {
      const toggle = event.target.closest('[data-cross-sell-toggle]');
      if (toggle) {
        this.toggle(toggle.closest('[data-cross-sell-item]'), toggle);
        return;
      }
      const button = event.target.closest('[data-cross-sell-add]');
      if (!button || button.getAttribute('aria-disabled') === 'true') return;
      const item = button.closest('[data-cross-sell-item]');
      if (!item.dataset.variantId) {
        if (item.dataset.unavailable) {
          this.setError(item, this.dataset.msgUnavailable || '');
          return;
        }
        const chosen = CartCrossSell.selection(item);
        const groups = [...item.querySelectorAll('[data-cross-sell-option]')];
        const missing = groups.filter((group, i) => !chosen[i]);
        this.setError(item, (this.dataset.msgChoose || '').replace('[option]', missing.map((g) => g.dataset.optionLabel).join(', ')));
        focusInPlace(missing[0]?.querySelector('input:not([disabled]), select'));
        return;
      }
      this.add(item, button);
    }

    setError(item, message) {
      const error = item.querySelector('[data-cross-sell-error]');
      if (!error) return;
      error.textContent = message;
      error.hidden = !message;
    }

    sectionsToRender() {
      if (this.context === 'drawer') {
        return [
          { id: 'cart-drawer', section: 'cart-drawer' },
          { id: 'cart-icon-bubble', section: 'cart-icon-bubble' },
        ];
      }
      const items = document.getElementById('main-cart-items');
      const footer = document.getElementById('main-cart-footer');
      return [
        items && { id: 'main-cart-items', section: items.dataset.id, selector: '.js-contents' },
        footer && { id: 'main-cart-footer', section: footer.dataset.id, selector: '.js-contents' },
        { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
        { id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' },
        // the layout renders the drawer on /cart too; keep it in step with the page
        document.getElementById('CartDrawer') && { id: 'CartDrawer', section: 'cart-drawer', selector: '#CartDrawer' },
      ].filter(Boolean);
    }

    async add(item, button) {
      const sections = this.sectionsToRender();
      button.classList.add('loading');
      button.setAttribute('aria-disabled', 'true');
      button.querySelector('.loading__spinner')?.classList.remove('hidden');
      this.setError(item, '');

      const properties = { _source: 'cart_fbt', _fbt_rule: item.dataset.rule };
      if (item.dataset.anchor) properties._fbt_anchor = item.dataset.anchor;

      let state;
      try {
        const response = await fetch(routes.cart_add_url, {
          ...fetchConfig(),
          body: JSON.stringify({
            items: [{ id: Number(item.dataset.variantId), quantity: 1, properties }],
            sections: sections.map((section) => section.section),
            sections_url: window.location.pathname,
          }),
        });
        state = await response.json();
        if (!response.ok || state.status) throw new Error(state.description || state.message);
      } catch (error) {
        button.classList.remove('loading');
        button.removeAttribute('aria-disabled');
        button.querySelector('.loading__spinner')?.classList.add('hidden');
        this.setError(item, error.message || window.cartStrings?.error || '');
        return;
      }

      const title = item.querySelector('.cart-cross-sell__title')?.textContent.trim() || '';
      // read before the re-render: the block may not exist afterwards (no suggestions left)
      const message = (this.dataset.msgAdded || '').replace('[product]', title);
      publishAnalytics('cart_cross_sell_added', {
        context: this.context,
        product: item.dataset.productId,
        variant: item.dataset.variantId,
        rule: item.dataset.rule,
      });

      // Focus target after the re-render: the next suggestion's button, else the drawer itself.
      const position = this.items().indexOf(item);
      if (this.context === 'drawer') {
        this.renderDrawer(state, position, message);
      } else {
        this.renderPage(state, sections, position, message);
      }
    }

    renderDrawer(state, position, message) {
      const drawer = document.querySelector('cart-drawer');
      const inner = document.getElementById('CartDrawer');
      const html = state.sections && state.sections['cart-drawer'];
      if (!drawer || !inner || !html) {
        window.location.href = routes.cart_url;
        return;
      }
      pendingFocus = { context: 'drawer', position, message, until: Date.now() + 4000 };
      // Swap the sections ourselves instead of renderContents(): that calls open(), and on an
      // already open drawer open()'s one-shot transitionend listener stays armed and later pulls
      // focus to .drawer__inner on the next unrelated transition.
      const parse = (markup, selector) => new DOMParser().parseFromString(markup, 'text/html').querySelector(selector);
      const fresh = parse(html, '#CartDrawer');
      if (fresh) inner.innerHTML = fresh.innerHTML;
      drawer.classList.remove('is-empty');
      inner.querySelector('#CartDrawer-Overlay')?.addEventListener('click', drawer.close.bind(drawer));
      const bubble = document.getElementById('cart-icon-bubble');
      const bubbleHtml = state.sections['cart-icon-bubble'];
      if (bubble && bubbleHtml) {
        const freshBubble = parse(bubbleHtml, '.shopify-section');
        if (freshBubble) bubble.innerHTML = freshBubble.innerHTML;
      }
      // No block left after the add (nothing more to suggest): focus the drawer instead.
      setTimeout(() => {
        if (!pendingFocus || document.querySelector('cart-cross-sell[data-context="drawer"]')) return;
        if (typeof trapFocus === 'function') trapFocus(inner, inner.querySelector('.drawer__inner'));
        CartCrossSell.announce('drawer', message);
        pendingFocus = null;
      }, 300);
    }

    restoreFocus() {
      if (!pendingFocus || pendingFocus.context !== this.context || Date.now() > pendingFocus.until) return;
      const { position, message } = pendingFocus;
      // after the current task, so a parent that is still being swapped in is in the document
      setTimeout(() => {
        if (!this.isConnected) return;
        // only where a rebuild dropped focus — never take it from where the shopper moved it
        const active = document.activeElement;
        if (active && active !== document.body && active.isConnected && !this.contains(active)) return;
        const target = CartCrossSell.focusTarget(this, position);
        if (!target) return;
        const container = document.getElementById('CartDrawer');
        if (this.context === 'drawer' && container && typeof trapFocus === 'function') {
          // trapFocus focuses with scrolling; set up the trap on the container, then focus in place
          trapFocus(container, container);
        }
        focusInPlace(target);
        CartCrossSell.announce(this.context, message);
      });
    }

    renderPage(state, sections, position, message) {
      pendingFocus = { context: 'page', position, message, until: Date.now() + 4000 };
      sections.forEach((section) => {
        const host = document.getElementById(section.id);
        const html = state.sections && state.sections[section.section];
        if (!host || !html) return;
        const target = host.querySelector(section.selector) || host;
        const source = new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector);
        if (source) target.innerHTML = source.innerHTML;
      });
      document.querySelector('cart-items')?.classList.remove('is-empty');
      document.getElementById('main-cart-footer')?.classList.remove('is-empty');
      if (!document.querySelector('cart-cross-sell[data-context="page"]')) {
        document.getElementById('main-cart-items')?.focus();
        CartCrossSell.announce('page', message);
        pendingFocus = null;
      }
    }

    static focusTarget(block, position) {
      if (!block) return null;
      const items = [...block.querySelectorAll('[data-cross-sell-item]')];
      const item = items[Math.min(position, items.length - 1)];
      return item?.querySelector('.cart-cross-sell__icon-button') || block.querySelector('h3[id]');
    }

    static announce(context, message) {
      if (!message) return;
      const block = document.querySelector(`cart-cross-sell[data-context="${context}"]`);
      const status = block?.querySelector('[data-cross-sell-status]')
        || document.getElementById(context === 'drawer' ? 'CartDrawer-LiveRegionText' : 'cart-live-region-text');
      if (status) status.textContent = message;
    }
  }

  customElements.define('cart-cross-sell', CartCrossSell);
}
